import type {
    Asset,
    IconAsset,
    MaskEdge,
    StencilMask,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { Container } from 'pixi.js';

import {
    getIsUserAllowedAccessToMoveAndResizeIcons,
    getIsUserAllowedAccessToMoveAndResizeMedia,
} from 'js/src/data/account/account-selectors';
import { WATERMARK_ASSET_NAME } from 'js/src/libs/consts';
import type { NormalizedRect } from 'js/src/libs/crop-utils';
import type { RootState } from 'js/src/libs/RootState';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

// Below this the crop window is unusably small and the resize handles overlap.
const MIN_MASK_WIDTH_PX = 5;
// The smallest span PolygonMask.resizeBy leaves on either axis, as a fraction of the reference
// resolution: it stops a resize there rather than turning the shape inside out.
const POLYGON_MASK_MIN_NORMALIZED_SPAN = 0.01;

export type CropMaskSnapshot = {
    mask:
        | {
              classType: typeof VideoData.POLYGON_MASK_TYPE;
              startPoints: number[];
              endPoints: number[];
          }
        | {
              classType: typeof VideoData.CIRCLE_MASK_TYPE;
              centerX: number;
              centerY: number;
              startRadius: number;
              endRadius: number;
          }
        | null;
    position: { x: number; y: number };
    anchor: { x: number; y: number };
    bounds: { x: number; y: number; width: number; height: number } | null;
};

/** What the user's permissions allow them to crop with a mask; see canCropWithMask. */
export type MaskCropPermissions = {
    canMoveAndResizeMedia: boolean;
    canMoveAndResizeIcons: boolean;
};

const getMaskCropPermissions = (state: RootState): MaskCropPermissions => ({
    canMoveAndResizeMedia: getIsUserAllowedAccessToMoveAndResizeMedia(state),
    canMoveAndResizeIcons: getIsUserAllowedAccessToMoveAndResizeIcons(state),
});

/**
 * A scene design's media the media permission lets the user move and resize by its mask. Only
 * media whose scene design already masks them: an unmasked media keeps its fixed frame.
 * A group lays out its children's boxes, and the watermark and a media linked to its scene group
 * are placed for the user, so none of those can be moved or resized on their own.
 */
const isMaskedSceneDesignMedia = (asset: TimeAwareMediaAsset) =>
    !!asset.mask &&
    VideoData.isStencilMask(asset.mask) &&
    !asset.linkToSceneGroup &&
    asset.name !== WATERMARK_ASSET_NAME &&
    !asset.getParentByType(VideoData.GROUP_ASSET_TYPE);

/**
 * A scene design's icon the icon permission lets the user crop, masked or not: a first crop gives
 * an unmasked one a mask over the icon it draws, as for a sticker. A waveform draws the voiceover
 * and a group lays out its children's boxes, so neither is cropped on its own.
 */
const isCroppableSceneDesignIcon = (asset: IconAsset) =>
    asset.playbackMode !== VideoData.PLAYBACK_WAVEFORM &&
    !asset.getParentByType(VideoData.GROUP_ASSET_TYPE);

/**
 * Whether the user may crop an asset with a mask: move and resize the mask over the content it
 * clips. The crop itself works the same for any media or icon; which of them may be cropped is a
 * product decision made only here. Any media or icon sticker may be, since no template depends on
 * one. A scene design's media may be only with the media move-and-resize permission, and only
 * when the scene design already masks it; a scene design's icon only with the icon one.
 */
export function canCropWithMask(
    asset: Asset | null | undefined,
    { canMoveAndResizeMedia, canMoveAndResizeIcons }: MaskCropPermissions,
): asset is TimeAwareMediaAsset | IconAsset {
    if (!asset || (!VideoData.isMediaAsset(asset) && !VideoData.isIconAsset(asset))) {
        return false;
    }
    if (VideoData.isStickerAsset(asset)) {
        return true;
    }
    if (VideoData.isMediaAsset(asset)) {
        return canMoveAndResizeMedia && isMaskedSceneDesignMedia(asset);
    }
    return canMoveAndResizeIcons && isCroppableSceneDesignIcon(asset);
}

/**
 * Whether the asset carries a crop the user may take off again. A sticker's mask is the user's own
 * crop; a scene design asset's mask belongs to the design, so it is resized but never removed.
 */
export function hasRemovableMaskCrop(
    asset: Asset | null | undefined,
    permissions: MaskCropPermissions,
): boolean {
    return (
        canCropWithMask(asset, permissions) &&
        VideoData.isStickerAsset(asset) &&
        !!asset.mask &&
        VideoData.isStencilMask(asset.mask)
    );
}

/**
 * The asset a mask crop operation acts on. The operations check the same policy the UI does, with
 * the user's permissions read from the store, so no caller can crop an asset the user may not.
 */
const getCroppableAsset = (
    luminary: Luminary,
    state: RootState,
    sceneId: string,
    assetId: string,
) => {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        // scene was deleted mid-gesture
        return null;
    }
    const asset = scene.getAssetById(assetId);
    if (!asset) {
        throw new Error('mask crop operation: asset not found');
    }
    if (!canCropWithMask(asset, getMaskCropPermissions(state))) {
        throw new Error('mask crop operation: asset cannot be cropped with a mask');
    }
    return { scene, asset };
};

const getCropStencilMask = (asset: { mask: Container | null }): StencilMask => {
    const mask = asset.mask;
    if (!mask || !VideoData.isStencilMask(mask)) {
        throw new Error('mask crop operation: asset has no stencil mask');
    }
    return mask;
};

type Rect = { x: number; y: number; width: number; height: number };

const getRectEdges = (rect: Rect): Record<MaskEdge, number> => ({
    left: rect.x,
    right: rect.x + rect.width,
    top: rect.y,
    bottom: rect.y + rect.height,
});

/**
 * Gives a media or icon asset that has no mask yet a mask covering exactly the content it draws.
 *
 * Cropping is expressed by shrinking a mask, so an unmasked asset has nothing to shrink. A polygon
 * at the content's own corners reveals precisely what was already visible, so this is a visual
 * no-op that turns the crop handles into an ordinary mask resize from there on. A mask only clips,
 * so the asset keeps its position and anchor.
 */
export const addMaskToMasklessAssetOperation = createLuminaryOperation(
    ({ luminary, getState }, { sceneId, assetId }: { sceneId: string; assetId: string }) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;
        if (asset.mask && VideoData.isStencilMask(asset.mask)) {
            return;
        }

        const { width, height } = luminary.referenceResolution;
        const content = asset.unclippedBounds;
        const left = content.x / width;
        const right = (content.x + content.width) / width;
        const top = content.y / height;
        const bottom = (content.y + content.height) / height;
        // same winding as a default PolygonMask: top-right, bottom-right, bottom-left, top-left
        const corners = [right, top, right, bottom, left, bottom, left, top];

        luminary.batchRun(() => {
            const mask = asset.setMask(VideoData.POLYGON_MASK_TYPE);
            mask.startPoints = corners;
            mask.endPoints = corners;
        });
    },
);

/**
 * Moves a cropped asset to the given `transformation.position`, taking its crop window along by
 * the same delta: the mask is placed in scene space, independently of the content it clips, so
 * moving either alone would change what the window shows. The target is absolute, like
 * setAssetPositionTransformationOperation, so a drag stays correct even when an earlier increment
 * was clamped.
 */
export const setCroppedAssetPositionOperation = createLuminaryOperation(
    (
        { luminary, getState },
        { sceneId, assetId, x, y }: { sceneId: string; assetId: string; x: number; y: number },
    ) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;
        const mask = getCropStencilMask(asset);
        const { position } = asset.transformation;
        const deltaX = x - position.x;
        const deltaY = y - position.y;
        luminary.batchRun(() => {
            mask.moveBy(deltaX, deltaY);
            position.x = x;
            position.y = y;
        });
    },
);

const isNearEdge = (edge: MaskEdge) => edge === 'left' || edge === 'top';

/**
 * Where an edge drag has to stop: a crop window can hide the content but has nothing to show past
 * it, so an edge never moves outward beyond the content's own edge.
 */
const clampEdgeTarget = (edge: MaskEdge, target: number, content: Rect) => {
    const contentEdge = getRectEdges(content)[edge];
    return isNearEdge(edge) ? Math.max(target, contentEdge) : Math.min(target, contentEdge);
};

/**
 * Drags one or more edges of a masked asset's crop window to absolute reference-resolution
 * coordinates in one batch: an edge handle moves one, a corner handle two. Each edge stops at the
 * content (see clampEdgeTarget). The mask only clips, so the content stays where it is: a crop
 * reveals or hides it, never slides it.
 */
export const resizeCropMaskEdgesOperation = createLuminaryOperation(
    (
        { luminary, getState },
        {
            sceneId,
            assetId,
            targets,
        }: { sceneId: string; assetId: string; targets: Partial<Record<MaskEdge, number>> },
    ) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;
        const mask = getCropStencilMask(asset);
        const content = asset.unclippedBounds;

        luminary.batchRun(() => {
            (Object.keys(targets) as MaskEdge[]).forEach((edge) => {
                const target = targets[edge];
                if (target === undefined) {
                    return;
                }
                // each edge is anchored at its opposite, so an earlier edge's resize doesn't move
                // this one, but the bounds are re-read to stay correct for shapes that scale whole
                const currentEdge = getRectEdges(mask.getMaxBounds())[edge];
                mask.resizeBy(edge, clampEdgeTarget(edge, target, content) - currentEdge);
            });
        });
    },
);

/**
 * Removes a sticker's crop. The mask only clipped the content, so the whole sticker shows again
 * exactly where it was drawn. A scene design asset's mask is part of the design, so it is never
 * removed (see hasRemovableMaskCrop).
 */
export const resetMaskCropOperation = createLuminaryOperation(
    ({ luminary, getState }, { sceneId, assetId }: { sceneId: string; assetId: string }) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;
        if (!VideoData.isStickerAsset(asset)) {
            throw new Error('mask crop operation: only a sticker crop can be removed');
        }
        if (!asset.mask || !VideoData.isStencilMask(asset.mask)) {
            return;
        }
        asset.mask = null;
    },
);

/**
 * Scales a cropped asset proportionally about its crop window's centre, for the scale handles it
 * keeps outside crop mode. The window, the content box and the content's position all
 * scale by the same factor about that centre, so the window keeps framing the same part of the
 * content: the mask only clips, so scaling it alone would only change the clip.
 */
export const setCroppedAssetSizeOperation = createLuminaryOperation(
    (
        { luminary, getState },
        { sceneId, assetId, width }: { sceneId: string; assetId: string; width: number },
    ) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;
        const mask = getCropStencilMask(asset);
        const bounds = mask.getMaxBounds();
        if (bounds.width <= 0 || bounds.height <= 0) {
            return;
        }
        // Every edge of a polygon stops at its smallest span, so a factor that shrinks either axis
        // past it would scale the window less than the content and shift what it frames.
        const reference = luminary.referenceResolution;
        const minFactor = VideoData.isPolygonMask(mask)
            ? Math.max(
                  MIN_MASK_WIDTH_PX / bounds.width,
                  (POLYGON_MASK_MIN_NORMALIZED_SPAN * reference.width) / bounds.width,
                  (POLYGON_MASK_MIN_NORMALIZED_SPAN * reference.height) / bounds.height,
              )
            : MIN_MASK_WIDTH_PX / bounds.width;
        const factor = Math.max(width / bounds.width, minFactor);
        const widthGrowth = (bounds.width * (factor - 1)) / 2;
        const heightGrowth = (bounds.height * (factor - 1)) / 2;
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        const { position } = asset.transformation;

        luminary.batchRun(() => {
            if (VideoData.isPolygonMask(mask)) {
                // grow each edge outward by the same amount so the centre stays put
                mask.resizeBy('right', widthGrowth);
                mask.resizeBy('left', -widthGrowth);
                mask.resizeBy('bottom', heightGrowth);
                mask.resizeBy('top', -heightGrowth);
            } else {
                // a circle has one size, so a single edge drag scales the whole shape
                mask.resizeBy('right', widthGrowth);
            }
            asset.maxWidth *= factor;
            asset.maxHeight *= factor;
            position.x = centerX + (position.x - centerX) * factor;
            position.y = centerY + (position.y - centerY) * factor;
        });
    },
);

/**
 * The image the crop ghost draws the asset with: the whole, unclipped content. A still image's
 * preview file already loads in an <img>; a video or Lottie has to be rasterised, which
 * needs the renderer, so this resolves to null where there is none and the ghost draws no image.
 */
export const readCropGhostImageOperation = createLuminaryOperation(
    async ({ luminary, getState }, sceneId: string, assetId: string): Promise<string | null> => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return null;
        }
        const { asset } = found;
        if (VideoData.isMediaAsset(asset) && asset.isVideo === false) {
            return asset.sourceData.preview_url;
        }
        if (!luminary.renderer) {
            return null;
        }
        const { base64 } = await asset.getBase64Data();
        return base64;
    },
);

export type MaskCropGeometry = {
    sprite: NormalizedRect;
    window: NormalizedRect | null;
};

/**
 * A mask-croppable asset's whole content box and, once cropped, its crop window, both normalized
 * to the scene the way the crop overlays are laid out. Neither is clamped to the scene: the ghost
 * has to reach wherever the content does. Null for anything the user may not crop with a mask.
 */
export function getMaskCropGeometry(
    asset: Asset | null | undefined,
    permissions: MaskCropPermissions,
): MaskCropGeometry | null {
    if (!canCropWithMask(asset, permissions)) {
        return null;
    }
    const { width, height } = asset.root.referenceResolution;
    if (!width || !height) {
        return null;
    }
    const normalize = (rect: Rect): NormalizedRect => ({
        x: rect.x / width,
        y: rect.y / height,
        width: rect.width / width,
        height: rect.height / height,
    });
    const mask = asset.mask && VideoData.isStencilMask(asset.mask) ? asset.mask : null;
    return {
        sprite: normalize(asset.unclippedBounds),
        window: mask ? normalize(mask.getMaxBounds()) : null,
    };
}

/**
 * Point-in-time read of a mask-croppable asset's mask geometry, position and anchor. The crop
 * session snapshots this on open (for cancel and dirty-detection) and the crop handles read it at
 * drag start. Returns null when the user may not crop the asset with a mask.
 */
export function getCropMaskSnapshot(
    asset: Asset | null | undefined,
    permissions: MaskCropPermissions,
): CropMaskSnapshot | null {
    if (!canCropWithMask(asset, permissions)) {
        return null;
    }

    const { position, anchor } = asset.transformation;
    const base = {
        position: { x: position.x, y: position.y },
        anchor: { x: anchor.x, y: anchor.y },
    };
    const mask = asset.mask;
    if (!mask || !VideoData.isStencilMask(mask)) {
        return { ...base, mask: null, bounds: null };
    }

    const maxBounds = mask.getMaxBounds();
    const bounds = {
        x: maxBounds.x,
        y: maxBounds.y,
        width: maxBounds.width,
        height: maxBounds.height,
    };
    if (VideoData.isPolygonMask(mask)) {
        return {
            ...base,
            bounds,
            mask: {
                classType: VideoData.POLYGON_MASK_TYPE,
                startPoints: [...mask.startPoints],
                endPoints: [...mask.endPoints],
            },
        };
    }
    if (VideoData.isCircleMask(mask)) {
        return {
            ...base,
            bounds,
            mask: {
                classType: VideoData.CIRCLE_MASK_TYPE,
                centerX: mask.centerX,
                centerY: mask.centerY,
                startRadius: mask.startRadius,
                endRadius: mask.endRadius,
            },
        };
    }
    return { ...base, mask: null, bounds };
}

export const readCropMaskStateOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string, assetId: string): CropMaskSnapshot | null =>
        getCropMaskSnapshot(
            luminary.getSceneById(sceneId)?.getAssetById(assetId),
            getMaskCropPermissions(getState()),
        ),
);

/**
 * Restores a mask-croppable asset to a previously read mask state — the crop session's cancel
 * path. A snapshot taken before the first crop has no mask, so restoring it removes the mask. The
 * position and anchor are restored with it, so the window and the content it frames always come
 * back as a pair.
 */
export const restoreCropMaskStateOperation = createLuminaryOperation(
    (
        { luminary, getState },
        {
            sceneId,
            assetId,
            snapshot,
        }: { sceneId: string; assetId: string; snapshot: CropMaskSnapshot },
    ) => {
        const found = getCroppableAsset(luminary, getState(), sceneId, assetId);
        if (!found) {
            return;
        }
        const { asset } = found;

        luminary.batchRun(() => {
            const target = snapshot.mask;
            if (!target) {
                asset.mask = null;
            } else if (target.classType === VideoData.POLYGON_MASK_TYPE) {
                const mask =
                    asset.mask && VideoData.isPolygonMask(asset.mask)
                        ? asset.mask
                        : asset.setMask(VideoData.POLYGON_MASK_TYPE);
                mask.startPoints = [...target.startPoints];
                mask.endPoints = [...target.endPoints];
            } else {
                const mask =
                    asset.mask && VideoData.isCircleMask(asset.mask)
                        ? asset.mask
                        : asset.setMask(VideoData.CIRCLE_MASK_TYPE);
                mask.centerX = target.centerX;
                mask.centerY = target.centerY;
                mask.startRadius = target.startRadius;
                mask.endRadius = target.endRadius;
            }
            asset.transformation.position.x = snapshot.position.x;
            asset.transformation.position.y = snapshot.position.y;
            asset.transformation.anchor.x = snapshot.anchor.x;
            asset.transformation.anchor.y = snapshot.anchor.y;
        });
    },
);
